<template>
  <div class="d-flex flex-nowrap overflow-auto crisisTableContainer">
    <div class="p-1 w-100 crisis-report-panel">
      <div class="d-flex">
        <div class="d-flex">
          <h5>{{ $t('operational_security.crisis_form.allocations_by_municipality') }} -
            <span class="font-italic">{{ data.name }}-{{ data.reference_number }}</span>
          </h5>
        </div>
        <feather-icon @click="showFiltersFn" icon="FilterIcon" size="16" class="ml-1 cursor-pointer"/>
      </div>

      <div class="crisis-report-filters" v-if="showFilters">
        <h5>{{ $t('operational_security.crisis_form.filters_by_region_city') }}</h5>
        <div class="d-flex flex-wrap">
          <div v-for="(region, index) in selected" :key="region.region" class="crisis-report-filter-group">
            <b-form-checkbox
              :name="`option-filter-region${index}`"
              v-model="region.checked"
              :disabled="region.isDisabled"
              @change="onRegionFilterChange(region, $event)"
            >
              {{ region.region }}
            </b-form-checkbox>

            <div class="crisis-report-city-list" v-if="!region.checked">
              <b-form-checkbox
                v-for="city in region.cities"
                :key="city.key"
                :name="`option-filter-city${index}`"
                v-model="city.checked"
                :disabled="city.isDisabled"
              >
                {{ city.name  }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <h5>{{ $t("custom_forms.categories") }}</h5>
          <div class="metric-grid ml-1">
            <div
              v-for="(metric, index) in metricsFilters"
              :key="metric.name"
              class="metric-item"
            >
              <b-form-checkbox
                :name="`option-filter-metric${index}`"
                v-model="metric.checked"
                :disabled="metric.isDisabled"
              >
                {{ metric.name }}
              </b-form-checkbox>
            </div>
          </div>
        </div>

        <div class="crisis-report-filter-section">
          <b-form-checkbox
            name="Only rows with data"
            v-model="showOnlyRowsWithData"
          >
            {{  $t('operational_security.crisis_form.only_rows_with_data') }}
          </b-form-checkbox>
        </div>
      </div>

      <SkeletonLoading
        v-if="isMasterDataLoading"
        :count="1"
        :height="230"
        :fullWidth="true"
      />

      <div class="crisis-report-grid-shell">
        <crisis-report-grid
        v-if="!isMasterDataLoading && !dataNotExists"
        :source="gridRows"
        :columns="gridColumns"
        height="100%"
        />

        <div class="w-100 h-100 d-flex justify-content-center align-items-center" v-if="dataNotExists">
        <p class="text-center">No existe información para esta crisis</p>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import {computed, getCurrentInstance, onMounted, ref, watch} from '@vue/composition-api';
import axios from "@/libs/axios";
import {useCrisisFormUpdate} from "./composables/useCrisisFormUpdateSocket";
import CrisisReportGrid from './CrisisReportGrid.vue';
import {
  formatCurrency,
  hasAnyRegionOrCitySelection,
  makeColumnGroup,
  makeGridColumn,
  makeRowHeaderColumn,
  makeSafeProp,
  onRegionSelectionChange,
  ROW_LABEL_PROP
} from './composables/useCrisisReportGrid';

export default {
  components: {
    CrisisReportGrid,
    SkeletonLoading
  },
  props: {
    data: {type: Object},
    slotId: {type: [String, Number], default: null},
  },
  setup(props) {
    const vm = getCurrentInstance().proxy;
    const {lastUpdate} = useCrisisFormUpdate(props.data.id);
    const masterData = ref([]);
    const isMasterDataLoading = ref(false);
    const dataNotExists = ref(false);
    const showFilters = ref(false);
    const selected = ref([]);
    const metricsFilters = ref([]);
    const showOnlyRowsWithData = ref(false);

    const tableColumns = [
      {key: 'total', label: 'operational_security.crisis_form.total'},
      {key: 'loss', label: 'operational_security.crisis_form.loss'},
      {key: 'operational', label: 'operational_security.crisis_form.operational'},
      {key: 'total_affecting_cash', label: 'operational_security.crisis_form.total_affecting_cash'},
      {key: 'cash_impact', label: 'operational_security.crisis_form.cash_impact', formatter: formatCurrency},
    ];

    onMounted(() => getAllocationByMunicipality());
    watch(lastUpdate, () => getAllocationByMunicipality());

    const tableRows = computed(() => {
      if (!masterData.value.length) return [];

      const sourceData = masterData.value[0].data;
      const topPriority = ['total_de_empleados'];
      const bottomPriority = ['total_emergency_kits_delivered', 'total_generators_delivered'];
      const csisPriority = ['csis_dias_en_campo', 'csis_voluntarios'];
      const allKeys = sourceData.map(item => item.metric);
      const middleKeys = allKeys.filter(key =>
        !topPriority.includes(key) &&
        !bottomPriority.includes(key) &&
        !csisPriority.includes(key)
      );
      const finalKeys = [...topPriority, ...middleKeys, ...bottomPriority, 'csis', ...csisPriority];

      return finalKeys.map(key => {
        if (key === 'csis') {
          return {key, label: 'CSIS'};
        }

        const item = sourceData.find(dataItem => dataItem.metric === key);
        return {
          key,
          label: item?.label || key
        };
      });
    });

    const gridData = computed(() => {
      const columns = [makeRowHeaderColumn()];
      const rowsByMetric = {};
      const hasAnySelection = hasAnyRegionOrCitySelection(selected.value);
      const enabledMetrics = metricsFilters.value
        .filter(metric => metric.checked)
        .map(metric => metric.name);
      const grouped = groupByRegion(masterData.value);

      grouped.forEach(({region, municipalities}) => {
        const selectedRegion = selected.value.find(item => item.region === region);
        if (!selectedRegion) return;

        if (hasAnySelection && selectedRegion.checked) {
          addRegionTotalColumn(columns, rowsByMetric, region, municipalities, enabledMetrics);
          return;
        }

        let visibleMunicipalities = municipalities;
        if (hasAnySelection) {
          visibleMunicipalities = municipalities.filter(municipality =>
            selectedRegion.cities.some(city =>
              city.key === municipality.municipality &&
              city.checked
            )
          );
        }

        visibleMunicipalities.forEach(municipality => {
          addMunicipalityColumnGroup(columns, rowsByMetric, municipality, enabledMetrics);
        });
      });

      const rows = Object.values(rowsByMetric);
      const visibleRows = showOnlyRowsWithData.value
        ? filterRowsWithData(rows)
        : rows;

      setResumeColumn(columns, visibleRows);

      return {
        columns,
        rows: visibleRows
      };
    });

    const gridColumns = computed(() => gridData.value.columns);
    const gridRows = computed(() => gridData.value.rows);

    function setResumeColumn(columns, visibleRows) {
      const summaryProp = makeSafeProp("summary", "total");

      columns.push(
        makeColumnGroup("Resumen", [
          makeGridColumn({
            prop: summaryProp,
            name: "Total",
            kind: "summary"
          })
        ])
      );
      const totalProps = [];

      columns.forEach(group => {
        if (!group.children) return;

        const totalChildren = group.children.filter(
          child => child.prop?.endsWith("_total")
        );

        if (totalChildren.length === 1) {
          totalProps.push(totalChildren[0].prop);
        } else {
          totalChildren
            .filter(child => child.name !== "Total")
            .forEach(child => totalProps.push(child.prop));
        }
      });
      visibleRows.forEach(row => {
        row[summaryProp] = totalProps.reduce(
          (sum, prop) => sum + (Number(row[prop]) || 0),
          0
        );
      });
    }

    function addRegionTotalColumn(columns, rowsByMetric, region, municipalities, enabledMetrics) {
      const prop = makeSafeProp('municipality-report', region, 'region-total');
      columns.push(makeColumnGroup(region, [
        makeGridColumn({
          prop,
          name: 'Total',
          kind: 'total'
        })
      ]));

      tableRows.value.forEach(row => {
        if (!isMetricEnabled(row, municipalities[0], enabledMetrics)) return;
        const gridRow = getMetricRow(rowsByMetric, row);
        const dataItem = municipalities
          .map(municipality => municipality.data.find(item => item.metric === row.key))
          .find(Boolean);

        gridRow[prop] = row.key === 'csis' ? '' : dataItem?.region_total ?? 0;
      });
    }

    function addMunicipalityColumnGroup(columns, rowsByMetric, municipality, enabledMetrics) {
      const children = tableColumns.map(column => makeGridColumn({
        prop: makeSafeProp('municipality-report', municipality.region, municipality.municipality, column.key),
        name: vm.$t(column.label),
        formatter: column.formatter || null
      }));
      const totalProp = makeSafeProp('municipality-report', municipality.region, municipality.municipality, 'total');

      children.push(makeGridColumn({
        prop: totalProp,
        name: municipality.region,
        kind: 'total'
      }));
      columns.push(makeColumnGroup(municipality.municipality, children));

      tableRows.value.forEach(row => {
        if (!isMetricEnabled(row, municipality, enabledMetrics)) return;
        const gridRow = getMetricRow(rowsByMetric, row);
        const item = municipality.data.find(dataItem => dataItem.metric === row.key);

        tableColumns.forEach(column => {
          const prop = makeSafeProp('municipality-report', municipality.region, municipality.municipality, column.key);
          gridRow[prop] = getMetricValue(row.key, item, column.key);
        });

        gridRow[totalProp] = row.key === 'csis' ? '' : item?.region_total ?? 0;
      });
    }

    function getMetricValue(rowKey, item, columnKey) {
      if (rowKey === 'csis') {
        return columnKey === 'total' ? 'Total' : '';
      }

      return item?.[columnKey] ?? 0;
    }

    function isMetricEnabled(row, municipality, enabledMetrics) {
      if (enabledMetrics.length === 0) return true;
      if (row.key === 'csis') return false;
      const item = municipality?.data.find(dataItem => dataItem.metric === row.key);

      return enabledMetrics.some(metric =>
        item?.label?.includes(metric)
      );
    }

    function getMetricRow(rowsByMetric, row) {
      if (!rowsByMetric[row.key]) {
        rowsByMetric[row.key] = {
          [ROW_LABEL_PROP]: row.label
        };
      }

      return rowsByMetric[row.key];
    }

    function filterRowsWithData(rows) {
      return rows.filter(row =>
        Object.keys(row).some(key =>
          key !== ROW_LABEL_PROP &&
          typeof row[key] === 'number' &&
          row[key] > 0
        )
      );
    }

    async function getAllocationByMunicipality() {
      try {
        isMasterDataLoading.value = true;
        dataNotExists.value = false;
        const {data} = await axios.get(`/v1/operational-security-crisis/${props.data.id}/damages-losses`);
        masterData.value = data.data || [];
        if (masterData.value.length === 0) dataNotExists.value = true;
        setFilterOptions();
        setMetricsFilters();
      } catch (error) {
        dataNotExists.value = true;
      } finally {
        isMasterDataLoading.value = false;
      }
    }

    function groupByRegion(data) {
      const map = {};

      data.forEach(item => {
        if (!map[item.region]) {
          map[item.region] = [];
        }

        map[item.region].push(item);
      });

      return Object.entries(map).map(([region, municipalities]) => ({
        region,
        municipalities
      }));
    }

    function setFilterOptions() {
      selected.value = groupByRegion(masterData.value).map(({region, municipalities}) => ({
        region,
        checked: false,
        isDisabled: false,
        cities: municipalities.map(municipality => ({
          key: municipality.municipality,
          name: municipality.municipality,
          checked: false,
          isDisabled: false
        }))
      }));
    }

    function showFiltersFn() {
      showFilters.value = !showFilters.value;
    }

    function onRegionFilterChange(region, checked) {
      onRegionSelectionChange(region, checked);
    }

    function setMetricsFilters() {
      const uniqueMetrics = [
        ...new Set(
          masterData.value.flatMap(item =>
            item.data.map(metricItem => metricItem.label.split(' - ')[0])
          )
        )
      ];

      metricsFilters.value = uniqueMetrics.map(metric => ({
        name: metric,
        checked: false,
        isDisabled: false
      }));
    }

    return {
      gridColumns,
      gridRows,
      isMasterDataLoading,
      dataNotExists,
      showFilters,
      selected,
      metricsFilters,
      showOnlyRowsWithData,
      showFiltersFn,
      onRegionFilterChange
    };
  }
};
</script>

<style scoped lang="scss">
.crisisTableContainer {
  height: 100%;
  min-height: 0;
  background-color: #fff;
}

.crisis-report-panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.crisis-report-grid-shell {
  flex: 1 1 auto;
  min-height: 420px;
  height: calc(100vh - 240px);
}

.crisis-report-filters {
  margin: 12px 0 16px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.crisis-report-filter-group {
  margin: 0 18px 14px 0;
}

.crisis-report-city-list {
  margin: 8px 0 0 16px;
}

.crisis-report-filter-section {
  margin-top: 14px;
}

.metric-grid {
  max-width: 650px;
  max-height: 150px;
  display: flex;
  flex-wrap: wrap;
}

.metric-item {
  width: 100px;
}
</style>
